/**
 * @fileoverview DocumentosModal
 * @description Modal para gestionar documentos de objetos Premium.
 * Permite subir, ver, compartir y eliminar documentos.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import {
  listarDocumentos,
  obtenerDocumento,
  subirDocumento,
  regenerarToken,
  eliminarDocumento,
  formatFileSize,
  getFileIcon,
  getFileExtension,
  guardarContacto,
  eliminarContacto,
  guardarRecordatorio,
  eliminarRecordatorio,
  analizarDocumentoRecordatorio,
  crearRecordatoriosLoteDocumento,
  obtenerRecordatoriosObjeto,
  actualizarRecordatorioObjeto,
  eliminarRecordatorioObjeto,
  eliminarGrupoRecordatorios,
  descargarWalletPassDocumento,
  compartirRecordatorios,
  revocarLinkRecordatorios
} from '../../services/api/modules/documentos';

export default function DocumentosModal({ show, onClose, objetoId, objetoNombre }) {
  const navigate = useNavigate();

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [documentos, setDocumentos] = useState([]);
  const [espacio, setEspacio] = useState({ usedMB: '0', maxMB: '60', percentUsed: '0' });

  // Estados para subir documento(s)
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [archivosParaSubir, setArchivosParaSubir] = useState([]); // [{ file, nombre, status: 'pending'|'uploading'|'done'|'error', progress: 0, error: '' }]
  const [uploadIndiceActual, setUploadIndiceActual] = useState(-1);

  // Estados para compartir
  const [documentoCompartir, setDocumentoCompartir] = useState(null);
  const [linksCompartir, setLinksCompartir] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [qrModal, setQrModal] = useState({ show: false, url: '', title: '' });

  // Estados para eliminar
  const [documentoEliminar, setDocumentoEliminar] = useState(null);
  const [confirmacionBorrar, setConfirmacionBorrar] = useState('');
  const [eliminando, setEliminando] = useState(false);

  // Estados para contacto
  const [documentoContacto, setDocumentoContacto] = useState(null);
  const [contactoForm, setContactoForm] = useState({
    nombre: '', rol: '', telefono: '', email: '', notas: ''
  });
  const [guardandoContacto, setGuardandoContacto] = useState(false);

  // Estados para recordatorio
  const [documentoRecordatorio, setDocumentoRecordatorio] = useState(null);
  const [recordatorioForm, setRecordatorioForm] = useState({
    descripcion: '', fechaVencimiento: '', diasAnticipacion: '15'
  });
  const [guardandoRecordatorio, setGuardandoRecordatorio] = useState(false);

  // Estados para selector de método y análisis IA
  const [recordatorioMetodo, setRecordatorioMetodo] = useState(null); // null | 'selector' | 'manual' | 'auto'
  const [analizandoDoc, setAnalizandoDoc] = useState(false);
  const [analisisError, setAnalisisError] = useState(null);
  const [analisisResultado, setAnalisisResultado] = useState(null);
  const [recurrenciaForm, setRecurrenciaForm] = useState('unico');

  // Estados para pagos múltiples
  const [pagosSeleccionados, setPagosSeleccionados] = useState({});
  const [montoEditable, setMontoEditable] = useState('');

  // Estados para recordatorios de objeto (subcollection)
  const [recordatoriosObjeto, setRecordatoriosObjeto] = useState([]);
  const [verRecordatoriosDoc, setVerRecordatoriosDoc] = useState(null); // doc para ver lista
  const [editandoRecObj, setEditandoRecObj] = useState(null); // recordatorio en edición
  const [editRecObjForm, setEditRecObjForm] = useState({ nombrePersonalizado: '', fechaUnica: '', monto: '', notas: '' });
  const [guardandoRecObj, setGuardandoRecObj] = useState(false);
  const [eliminandoRecObj, setEliminandoRecObj] = useState(null);

  // Estados para selección y compartir recordatorios
  const [selectedRecIds, setSelectedRecIds] = useState(new Set());
  const [compartirModal, setCompartirModal] = useState(null); // { shareUrl, token, qrDataUrl, count }
  const [loadingCompartir, setLoadingCompartir] = useState(false);

  // Estados para Apple Wallet
  const [walletOffer, setWalletOffer] = useState(null); // { docId, recordatorioIds: [], isUpdate? }
  const [descargandoWallet, setDescargandoWallet] = useState(false);
  const [descargandoWalletRecId, setDescargandoWalletRecId] = useState(null);

  // Cargar documentos
  const cargarDocumentos = useCallback(async () => {
    if (!objetoId) return;

    setLoading(true);
    const [docsResult, recResult] = await Promise.all([
      listarDocumentos(objetoId),
      obtenerRecordatoriosObjeto(objetoId)
    ]);

    if (docsResult.error) {
      toast.error(docsResult.error);
    } else {
      setDocumentos(docsResult.documentos || []);
      setEspacio(docsResult.espacio || { usedMB: '0', maxMB: '60', percentUsed: '0' });
    }

    if (!recResult.error) {
      setRecordatoriosObjeto(recResult.data || []);
    }
    setLoading(false);
  }, [objetoId]);

  useEffect(() => {
    if (show && objetoId) {
      cargarDocumentos();
    }
  }, [show, objetoId, cargarDocumentos]);

  // Contar recordatorios por documentoId
  const recordatoriosPorDoc = useMemo(() => {
    const map = {};
    recordatoriosObjeto.forEach(r => {
      if (r.documentoId) {
        if (!map[r.documentoId]) map[r.documentoId] = [];
        map[r.documentoId].push(r);
      }
    });
    return map;
  }, [recordatoriosObjeto]);

  // Sincronizar formulario de recordatorio cuando cambia documentoRecordatorio
  useEffect(() => {
    if (documentoRecordatorio?.recordatorio) {
      const rec = documentoRecordatorio.recordatorio;
      // Formatear fecha para input date
      // El timestamp de Firestore viene serializado como { _seconds, _nanoseconds }
      let fechaStr = '';
      if (rec.fechaVencimiento) {
        const fv = rec.fechaVencimiento;
        const fecha = fv._seconds
          ? new Date(fv._seconds * 1000)
          : fv.toDate
            ? fv.toDate()
            : new Date(fv);
        fechaStr = fecha.toISOString().split('T')[0];
      }
      setRecordatorioForm({
        descripcion: rec.descripcion || '',
        fechaVencimiento: fechaStr,
        diasAnticipacion: String(rec.diasAnticipacion || '15')
      });
    } else if (documentoRecordatorio && !documentoRecordatorio.recordatorio) {
      // Documento sin recordatorio, limpiar formulario
      setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
    }
  }, [documentoRecordatorio]);

  // Sincronizar formulario de contacto cuando cambia documentoContacto
  useEffect(() => {
    if (documentoContacto?.contacto) {
      const con = documentoContacto.contacto;
      setContactoForm({
        nombre: con.nombre || '',
        rol: con.rol || '',
        telefono: con.telefono || '',
        email: con.email || '',
        notas: con.notas || ''
      });
    } else if (documentoContacto && !documentoContacto.contacto) {
      // Documento sin contacto, limpiar formulario
      setContactoForm({ nombre: '', rol: '', telefono: '', email: '', notas: '' });
    }
  }, [documentoContacto]);

  // Reset al cerrar
  const handleClose = () => {
    setShowUploadForm(false);
    setArchivosParaSubir([]);
    setUploadIndiceActual(-1);
    setDocumentoCompartir(null);
    setLinksCompartir(null);
    setDocumentoEliminar(null);
    setConfirmacionBorrar('');
    setDocumentoContacto(null);
    setContactoForm({ nombre: '', rol: '', telefono: '', email: '', notas: '' });
    setDocumentoRecordatorio(null);
    setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
    setRecordatorioMetodo(null);
    setAnalizandoDoc(false);
    setAnalisisError(null);
    setAnalisisResultado(null);
    setRecurrenciaForm('unico');
    setPagosSeleccionados({});
    setMontoEditable('');
    setVerRecordatoriosDoc(null);
    setEditandoRecObj(null);
    setEliminandoRecObj(null);
    setWalletOffer(null);
    setDescargandoWallet(false);
    setDescargandoWalletRecId(null);
    onClose();
  };

  // === SUBIR DOCUMENTO(S) ===
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 20 * 1024 * 1024; // 20MB per file
    const maxTotalMB = parseFloat(espacio.maxMB) || 60;
    const usedMB = parseFloat(espacio.usedMB) || 0;
    const yaSeleccionadosBytes = archivosParaSubir.reduce((sum, a) => sum + a.file.size, 0);
    const remainingBytes = (maxTotalMB - usedMB) * 1024 * 1024 - yaSeleccionadosBytes;

    const validados = [];
    const errores = [];
    let totalSize = 0;

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        errores.push(`${file.name}: formato no permitido`);
        continue;
      }
      if (file.size > maxSize) {
        errores.push(`${file.name}: excede 20MB`);
        continue;
      }
      totalSize += file.size;
      validados.push({
        file,
        nombre: file.name.replace(/\.[^/.]+$/, ''),
        status: 'pending',
        progress: 0,
        error: ''
      });
    }

    if (totalSize > remainingBytes) {
      toast.error(`Los archivos seleccionados (${formatFileSize(totalSize)}) exceden el espacio disponible (${(maxTotalMB - usedMB).toFixed(1)} MB libres)`);
      e.target.value = '';
      return;
    }

    if (errores.length > 0) {
      errores.forEach(err => toast.error(err));
    }

    if (validados.length === 0) {
      e.target.value = '';
      return;
    }

    setArchivosParaSubir(prev => [...prev, ...validados]);
    e.target.value = '';
  };

  const handleQuitarArchivo = (index) => {
    setArchivosParaSubir(prev => prev.filter((_, i) => i !== index));
  };

  const handleCambiarNombre = (index, nombre) => {
    setArchivosParaSubir(prev => prev.map((a, i) => i === index ? { ...a, nombre } : a));
  };

  const handleSubir = async () => {
    const sinNombre = archivosParaSubir.some(a => !a.nombre.trim());
    if (sinNombre) {
      toast.error('Todos los archivos necesitan un nombre');
      return;
    }

    setUploading(true);
    let exitosos = 0;
    let fallidos = 0;

    for (let i = 0; i < archivosParaSubir.length; i++) {
      const archivo = archivosParaSubir[i];
      setUploadIndiceActual(i);
      setArchivosParaSubir(prev => prev.map((a, idx) => idx === i ? { ...a, status: 'uploading', progress: 0 } : a));

      const result = await subirDocumento(
        objetoId,
        archivo.nombre.trim(),
        archivo.file,
        (progress) => {
          setArchivosParaSubir(prev => prev.map((a, idx) => idx === i ? { ...a, progress } : a));
        }
      );

      if (result.error) {
        fallidos++;
        setArchivosParaSubir(prev => prev.map((a, idx) => idx === i ? { ...a, status: 'error', error: result.error } : a));
      } else {
        exitosos++;
        setArchivosParaSubir(prev => prev.map((a, idx) => idx === i ? { ...a, status: 'done', progress: 100 } : a));
      }
    }

    setUploading(false);
    setUploadIndiceActual(-1);

    if (exitosos > 0) {
      toast.success(`${exitosos} documento${exitosos > 1 ? 's' : ''} subido${exitosos > 1 ? 's' : ''}`);
      cargarDocumentos();
    }
    if (fallidos > 0) {
      toast.error(`${fallidos} archivo${fallidos > 1 ? 's' : ''} fallaron al subir`);
    }
    if (fallidos === 0) {
      setShowUploadForm(false);
      setArchivosParaSubir([]);
    }
  };

  // === COMPARTIR ===
  const handleCompartir = async (doc) => {
    setDocumentoCompartir(doc);
    setLoadingLinks(true);

    const result = await obtenerDocumento(objetoId, doc.id);

    if (result.error) {
      toast.error(result.error);
      setDocumentoCompartir(null);
    } else {
      setLinksCompartir(result.links);
    }
    setLoadingLinks(false);
  };

  const handleCopiarLink = (link, tipo) => {
    navigator.clipboard.writeText(link);
    toast.success(`Link de ${tipo} copiado`);
  };

  const handleVerQR = async (url, title) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, { width: 256, margin: 2 });
      setQrModal({ show: true, url: qrDataUrl, title, link: url });
    } catch (error) {
      toast.error('Error al generar QR');
    }
  };

  const handleRegenerarToken = async (tipo) => {
    if (!documentoCompartir) return;

    const confirm = window.confirm(
      `¿Regenerar el link de ${tipo}? El link anterior dejará de funcionar.`
    );
    if (!confirm) return;

    setLoadingLinks(true);
    const result = await regenerarToken(objetoId, documentoCompartir.id, tipo);

    if (result.error) {
      toast.error(result.error);
    } else {
      setLinksCompartir(result.links);
      toast.success('Link regenerado');
    }
    setLoadingLinks(false);
  };

  // === ELIMINAR ===
  const handleEliminar = async () => {
    if (confirmacionBorrar !== 'borrar') {
      toast.error('Escribe "borrar" para confirmar');
      return;
    }

    setEliminando(true);
    const result = await eliminarDocumento(objetoId, documentoEliminar.id);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Documento eliminado');
      setDocumentoEliminar(null);
      setConfirmacionBorrar('');
      cargarDocumentos();
    }
    setEliminando(false);
  };

  // === VER / DESCARGAR ===
  const handleVer = async (doc) => {
    // Obtener el documento con tokens
    const result = await obtenerDocumento(objetoId, doc.id);
    if (result.error) {
      toast.error('Error al obtener documento');
      return;
    }
    // Extraer el token de la URL de consulta
    const tokenMatch = result.links.consulta.match(/\/d\/([^/]+)$/);
    if (tokenMatch) {
      // Navegar a la ruta del documento (se incrusta en la app)
      navigate(`/d/${tokenMatch[1]}`);
    } else {
      // Fallback: abrir en nueva pestaña
      window.open(result.links.consulta, '_blank');
    }
  };

  // === CONTACTO ===
  const handleAbrirContacto = async (doc) => {
    // Cargar el documento completo para obtener datos del contacto
    const result = await obtenerDocumento(objetoId, doc.id);
    if (result.error) {
      toast.error('Error al cargar documento');
      return;
    }
    // API devuelve los campos directamente (no envueltos en .documento)
    setDocumentoContacto({ ...doc, contacto: result.contacto });
    setContactoForm({
      nombre: result.contacto?.nombre || '',
      rol: result.contacto?.rol || '',
      telefono: result.contacto?.telefono || '',
      email: result.contacto?.email || '',
      notas: result.contacto?.notas || ''
    });
  };

  const handleGuardarContacto = async () => {
    // Al menos un campo debe tener contenido
    const tieneContenido = Object.values(contactoForm).some(v => v.trim());
    if (!tieneContenido) {
      toast.error('Ingresa al menos un dato del contacto');
      return;
    }

    setGuardandoContacto(true);
    const result = await guardarContacto(objetoId, documentoContacto.id, contactoForm);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Contacto guardado');
      setDocumentoContacto(null);
      setContactoForm({ nombre: '', rol: '', telefono: '', email: '', notas: '' });
      cargarDocumentos();
    }
    setGuardandoContacto(false);
  };

  const handleEliminarContacto = async () => {
    if (!window.confirm('¿Eliminar el contacto de este documento?')) return;

    setGuardandoContacto(true);
    const result = await eliminarContacto(objetoId, documentoContacto.id);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Contacto eliminado');
      setDocumentoContacto(null);
      setContactoForm({ nombre: '', rol: '', telefono: '', email: '', notas: '' });
      cargarDocumentos();
    }
    setGuardandoContacto(false);
  };

  // === RECORDATORIO ===
  const handleAbrirRecordatorio = async (doc) => {
    const result = await obtenerDocumento(objetoId, doc.id);
    if (result.error) {
      toast.error('Error al cargar documento');
      return;
    }
    // API devuelve los campos directamente (no envueltos en .documento)
    const docConRecordatorio = { ...doc, recordatorio: result.recordatorio };
    setDocumentoRecordatorio(docConRecordatorio);

    // If document already has a reminder, go straight to edit form
    if (result.recordatorio) {
      let fechaStr = '';
      if (result.recordatorio.fechaVencimiento) {
        const fv = result.recordatorio.fechaVencimiento;
        const fecha = fv._seconds
          ? new Date(fv._seconds * 1000)
          : fv.toDate
            ? fv.toDate()
            : new Date(fv);
        fechaStr = fecha.toISOString().split('T')[0];
      }
      setRecordatorioForm({
        descripcion: result.recordatorio.descripcion || '',
        fechaVencimiento: fechaStr,
        diasAnticipacion: String(result.recordatorio.diasAnticipacion || '15')
      });
      setRecordatorioMetodo('manual');
    } else {
      // New reminder: check if format supports AI analysis
      const analisisTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (analisisTypes.includes(doc.tipo)) {
        // Show two-option selector
        setRecordatorioMetodo('selector');
      } else {
        // Format not supported for AI, go straight to manual
        setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
        setRecordatorioMetodo('manual');
      }
    }
  };

  // Analizar documento con IA
  const handleAnalizarDocumento = async () => {
    if (!documentoRecordatorio) return;
    setRecordatorioMetodo('auto');
    setAnalizandoDoc(true);
    setAnalisisError(null);
    setAnalisisResultado(null);

    const result = await analizarDocumentoRecordatorio(objetoId, documentoRecordatorio.id);

    setAnalizandoDoc(false);

    if (result.error) {
      setAnalisisError(result.error);
      return;
    }

    if (result.encontrado === false) {
      setAnalisisError(result.razon || 'No pudimos encontrar fechas de vencimiento en este documento.');
      return;
    }

    // Success: pre-fill the form
    setAnalisisResultado(result);
    setRecordatorioForm({
      descripcion: result.descripcion || '',
      fechaVencimiento: result.fechaVencimiento || '',
      diasAnticipacion: '15'
    });
    setRecurrenciaForm(result.recurrencia || 'unico');

    // Initialize payment selections if pagosDetectados exists
    if (result.pagosDetectados && result.pagosDetectados.length > 0) {
      const selecciones = {};
      result.pagosDetectados.forEach((_, i) => { selecciones[i] = true; });
      setPagosSeleccionados(selecciones);
      setMontoEditable(result.montoPago != null ? String(result.montoPago) : '');
    } else {
      setPagosSeleccionados({});
      setMontoEditable('');
    }
  };

  // Go to manual from selector
  const handleRecordatorioManual = () => {
    setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
    setRecordatorioMetodo('manual');
  };

  // Back to selector
  const handleVolverSelector = () => {
    setRecordatorioMetodo('selector');
    setAnalisisError(null);
    setAnalisisResultado(null);
    setAnalizandoDoc(false);
  };

  // Save multiple payment reminders (batch)
  const handleGuardarPagosLote = async () => {
    if (!analisisResultado?.pagosDetectados) return;

    const pagosActivos = analisisResultado.pagosDetectados
      .filter((_, i) => pagosSeleccionados[i])
      .map(pago => ({
        fecha: pago.fecha,
        descripcion: pago.descripcion,
        monto: montoEditable ? parseFloat(montoEditable) : null,
        moneda: analisisResultado.moneda || 'MXN',
        notas: analisisResultado.notas || ''
      }));

    if (pagosActivos.length === 0) {
      toast.error('Selecciona al menos un recordatorio');
      return;
    }

    setGuardandoRecordatorio(true);
    const result = await crearRecordatoriosLoteDocumento(objetoId, {
      recordatorios: pagosActivos,
      documentoId: documentoRecordatorio?.id,
      documentoNombre: documentoRecordatorio?.nombre
    });

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(result.mensaje || `${pagosActivos.length} recordatorio${pagosActivos.length > 1 ? 's' : ''} creado${pagosActivos.length > 1 ? 's' : ''}`);
      const docId = documentoRecordatorio?.id;
      const recIds = (result.data || []).map(r => r.recordatorioId).filter(Boolean);
      setDocumentoRecordatorio(null);
      setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
      setRecordatorioMetodo(null);
      setAnalisisResultado(null);
      setPagosSeleccionados({});
      setMontoEditable('');
      setRecurrenciaForm('unico');
      cargarDocumentos();
      // Offer Apple Wallet pass on Apple devices
      if (docId && recIds.length > 0) {
        setWalletOffer({ docId, recordatorioIds: recIds });
      }
    }
    setGuardandoRecordatorio(false);
  };

  // === RECORDATORIOS DE OBJETO (lista) ===
  const handleVerRecordatoriosDoc = (doc) => {
    setVerRecordatoriosDoc(doc);
    setEditandoRecObj(null);
    setEditRecObjForm({ nombrePersonalizado: '', fechaUnica: '', monto: '', notas: '' });
  };

  const handleEditarRecObj = (rec) => {
    setEditandoRecObj(rec);
    setEditRecObjForm({
      nombrePersonalizado: rec.nombrePersonalizado || '',
      fechaUnica: rec.fechaUnica || '',
      monto: rec.monto != null ? String(rec.monto) : '',
      notas: rec.notas || ''
    });
  };

  const handleGuardarRecObj = async () => {
    if (!editandoRecObj) return;
    setGuardandoRecObj(true);
    const datos = {
      nombrePersonalizado: editRecObjForm.nombrePersonalizado,
      fechaUnica: editRecObjForm.fechaUnica,
      notas: editRecObjForm.notas,
      monto: editRecObjForm.monto ? parseFloat(editRecObjForm.monto) : null
    };
    const result = await actualizarRecordatorioObjeto(objetoId, editandoRecObj.recordatorioId, datos);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Recordatorio actualizado');
      const docId = verRecordatoriosDoc?.id;
      const recId = editandoRecObj.recordatorioId;
      setEditandoRecObj(null);
      cargarDocumentos();
      // Suggest wallet pass update
      if (docId && recId) {
        setWalletOffer({ docId, recordatorioIds: [recId], isUpdate: true });
      }
    }
    setGuardandoRecObj(false);
  };

  const handleEliminarRecObj = async (rec) => {
    if (!window.confirm('¿Eliminar este recordatorio?')) return;
    setEliminandoRecObj(rec.recordatorioId);
    const result = await eliminarRecordatorioObjeto(objetoId, rec.recordatorioId);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Recordatorio eliminado');
      cargarDocumentos();
    }
    setEliminandoRecObj(null);
  };

  const handleEliminarGrupoRec = async (grupoId) => {
    const recsGrupo = recordatoriosObjeto.filter(r => r.grupoRecordatorio === grupoId);
    if (!window.confirm(`¿Eliminar los ${recsGrupo.length} recordatorios de este documento?`)) return;
    setGuardandoRecObj(true);
    const result = await eliminarGrupoRecordatorios(objetoId, grupoId);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(result.mensaje || 'Recordatorios eliminados');
      setVerRecordatoriosDoc(null);
      cargarDocumentos();
    }
    setGuardandoRecObj(false);
  };

  // Apple Wallet pass direct download (from recordatorios list)
  const handleDescargarWalletPassDirecto = async (docId, recId) => {
    setDescargandoWalletRecId(recId);
    const result = await descargarWalletPassDocumento(objetoId, docId, recId);
    if (result.error) {
      toast.error(result.error);
    } else if (result.blob) {
      const blobUrl = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'lemmontags-recordatorio.pkpass';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
    setDescargandoWalletRecId(null);
  };

  // Apple Wallet pass download (from offer modal)
  const handleDescargarWalletPass = async (recId) => {
    if (!walletOffer?.docId) return;
    setDescargandoWallet(true);
    const result = await descargarWalletPassDocumento(objetoId, walletOffer.docId, recId);
    if (result.error) {
      toast.error(result.error);
    } else if (result.blob) {
      const blobUrl = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'lemmontags-recordatorio.pkpass';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      setWalletOffer(null);
    }
    setDescargandoWallet(false);
  };

  // Compartir recordatorios seleccionados
  const handleToggleRecSelect = (recId) => {
    setSelectedRecIds(prev => {
      const next = new Set(prev);
      if (next.has(recId)) {
        next.delete(recId);
      } else {
        next.add(recId);
      }
      return next;
    });
  };

  const handleSelectAllRecs = (recs) => {
    const allIds = recs.map(r => r.recordatorioId);
    const allSelected = allIds.every(id => selectedRecIds.has(id));
    if (allSelected) {
      setSelectedRecIds(new Set());
    } else {
      setSelectedRecIds(new Set(allIds));
    }
  };

  const handleCompartirRecordatorios = async () => {
    if (selectedRecIds.size === 0) return;
    setLoadingCompartir(true);
    const result = await compartirRecordatorios(objetoId, Array.from(selectedRecIds));
    if (result.error) {
      toast.error(result.error);
      setLoadingCompartir(false);
      return;
    }

    // Generate QR code
    let qrDataUrl = '';
    try {
      qrDataUrl = await QRCode.toDataURL(result.shareUrl, { width: 256, margin: 2 });
    } catch (err) {
      console.error('Error generando QR:', err);
    }

    setCompartirModal({
      shareUrl: result.shareUrl,
      token: result.token,
      qrDataUrl,
      count: result.recordatoriosCompartidos || selectedRecIds.size
    });
    setLoadingCompartir(false);
  };

  const handleRevocarLink = async () => {
    if (!compartirModal?.token) return;
    if (!window.confirm('Al revocar el link, dejará de funcionar. ¿Continuar?')) return;

    const result = await revocarLinkRecordatorios(objetoId, compartirModal.token);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Link revocado');
    setCompartirModal(null);
  };

  const handleCopiarLinkCompartir = () => {
    if (!compartirModal?.shareUrl) return;
    navigator.clipboard.writeText(compartirModal.shareUrl);
    toast.success('Link copiado al portapapeles');
  };

  const handleGuardarRecordatorio = async () => {
    if (!recordatorioForm.descripcion.trim()) {
      toast.error('Ingresa una descripción');
      return;
    }
    if (!recordatorioForm.fechaVencimiento) {
      toast.error('Selecciona la fecha de vencimiento');
      return;
    }

    // Validar que la fecha sea futura
    const fechaVenc = new Date(recordatorioForm.fechaVencimiento);
    if (fechaVenc <= new Date()) {
      toast.error('La fecha debe ser futura');
      return;
    }

    setGuardandoRecordatorio(true);
    const result = await guardarRecordatorio(objetoId, documentoRecordatorio.id, recordatorioForm);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Recordatorio guardado');
      setDocumentoRecordatorio(null);
      setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
      setRecordatorioMetodo(null);
      setAnalisisResultado(null);
      setRecurrenciaForm('unico');
      cargarDocumentos();
    }
    setGuardandoRecordatorio(false);
  };

  const handleEliminarRecordatorio = async () => {
    if (!window.confirm('¿Eliminar el recordatorio de este documento?')) return;

    setGuardandoRecordatorio(true);
    const result = await eliminarRecordatorio(objetoId, documentoRecordatorio.id);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Recordatorio eliminado');
      setDocumentoRecordatorio(null);
      setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
      setRecordatorioMetodo(null);
      setAnalisisResultado(null);
      setRecurrenciaForm('unico');
      cargarDocumentos();
    }
    setGuardandoRecordatorio(false);
  };

  if (!show) return null;

  // Usar Portal para renderizar fuera del stacking context del panel
  return createPortal(
    <>
      {/* Responsive fullscreen styles for mobile */}
      <style>{`
        @media (max-width: 767.98px) {
          .documentos-modal-main.modal-fullscreen-md-down {
            max-height: none !important;
          }
          .documentos-modal-main.modal-fullscreen-md-down > .modal-content {
            max-height: none !important;
            height: 100%;
          }
          .documentos-modal-main.modal-fullscreen-md-down > .modal-content > .modal-body {
            max-height: none !important;
            flex: 1;
            overflow-y: auto;
          }
        }
      `}</style>

      {/* Modal Principal */}
      <div className="modal show d-block" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 99999,
        overflowY: 'auto'
      }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable modal-fullscreen-md-down documentos-modal-main"
          style={{ maxHeight: '90vh' }}>
          <div className="modal-content" style={{ maxHeight: '85vh' }}>
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-folder2-open me-2"></i>
                Documentos de {objetoNombre || 'Objeto'}
              </h5>
              <button type="button" className="btn-close" onClick={handleClose}></button>
            </div>

            {/* Body */}
            <div className="modal-body" style={{ maxHeight: 'calc(85vh - 130px)', overflowY: 'auto' }}>
              {/* Barra de espacio */}
              <div className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Espacio usado</span>
                  <span>{espacio.usedMB} MB / {espacio.maxMB} MB</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div
                    className={`progress-bar ${parseFloat(espacio.percentUsed) > 90 ? 'bg-danger' : 'bg-primary'}`}
                    style={{ width: `${espacio.percentUsed}%` }}
                  ></div>
                </div>
              </div>

              {/* Botón agregar */}
              {!showUploadForm && (
                <button
                  className="btn btn-primary mb-3"
                  onClick={() => setShowUploadForm(true)}
                  disabled={parseFloat(espacio.percentUsed) >= 100}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Agregar documentos
                </button>
              )}

              {/* Formulario de subida */}
              {showUploadForm && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title">Subir documentos</h6>

                    {archivosParaSubir.length === 0 && (
                      <div className="mb-2">
                        <label className="form-label small">Archivos (PDF, JPG, PNG - máx 20MB c/u)</label>
                        <input
                          type="file"
                          className="form-control form-control-sm"
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                          onChange={handleFileSelect}
                          disabled={uploading}
                        />
                      </div>
                    )}

                    {/* Lista de archivos seleccionados */}
                    {archivosParaSubir.length > 0 && (
                      <div className="mb-2">
                        <label className="form-label small">
                          {uploading
                            ? `Subiendo ${uploadIndiceActual + 1} de ${archivosParaSubir.length}...`
                            : `${archivosParaSubir.length} archivo${archivosParaSubir.length > 1 ? 's' : ''} seleccionado${archivosParaSubir.length > 1 ? 's' : ''}`}
                        </label>
                        <div className="list-group list-group-flush">
                          {archivosParaSubir.map((archivo, idx) => (
                            <div key={idx} className="list-group-item px-0 py-2">
                              <div className="d-flex align-items-center gap-2">
                                {/* Status icon */}
                                {archivo.status === 'done' && <i className="bi bi-check-circle-fill text-success"></i>}
                                {archivo.status === 'error' && <i className="bi bi-x-circle-fill text-danger"></i>}
                                {archivo.status === 'uploading' && <span className="spinner-border spinner-border-sm text-primary"></span>}
                                {archivo.status === 'pending' && <i className="bi bi-file-earmark text-muted"></i>}

                                <div className="flex-grow-1">
                                  {/* Editable name (only before upload starts) */}
                                  {!uploading ? (
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={archivo.nombre}
                                      onChange={(e) => handleCambiarNombre(idx, e.target.value)}
                                      maxLength={100}
                                      placeholder="Nombre del documento"
                                    />
                                  ) : (
                                    <div className="fw-medium" style={{ fontSize: '0.9rem' }}>{archivo.nombre}</div>
                                  )}
                                  <small className="text-muted">
                                    {archivo.file.name} ({formatFileSize(archivo.file.size)})
                                  </small>
                                  {archivo.error && (
                                    <small className="text-danger d-block">{archivo.error}</small>
                                  )}
                                </div>

                                {/* Remove button (only before upload) */}
                                {!uploading && (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleQuitarArchivo(idx)}
                                    style={{ padding: '2px 6px' }}
                                    title="Quitar"
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                )}
                              </div>

                              {/* Progress bar */}
                              {archivo.status === 'uploading' && (
                                <div className="progress mt-1" style={{ height: '4px' }}>
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    style={{ width: `${archivo.progress}%` }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Add more files button */}
                        {!uploading && (
                          <div className="mt-2">
                            <label className="btn btn-sm btn-outline-primary" style={{ cursor: 'pointer' }}>
                              <i className="bi bi-plus-lg me-1"></i>Agregar más
                              <input
                                type="file"
                                className="d-none"
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple
                                onChange={handleFileSelect}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleSubir}
                        disabled={uploading || archivosParaSubir.length === 0}
                      >
                        {uploading
                          ? `Subiendo (${uploadIndiceActual + 1}/${archivosParaSubir.length})...`
                          : `Subir${archivosParaSubir.length > 1 ? ` ${archivosParaSubir.length} archivos` : ''}`}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowUploadForm(false);
                          setArchivosParaSubir([]);
                          setUploadIndiceActual(-1);
                        }}
                        disabled={uploading}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de documentos */}
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              ) : documentos.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-folder2 fs-1"></i>
                  <p className="mt-2">No hay documentos</p>
                </div>
              ) : (
                <div className="list-group">
                  {documentos.map((doc) => (
                    <div key={doc.id} className="list-group-item">
                      {/* Encabezado del documento */}
                      <div className="d-flex align-items-start justify-content-between">
                        <div className="d-flex align-items-center flex-grow-1 me-2">
                          <i className={`${getFileIcon(doc.tipo)} fs-4 me-2 text-muted`}></i>
                          <div className="flex-grow-1">
                            <div className="fw-medium">{doc.nombre}</div>
                            <small className="text-muted">
                              {getFileExtension(doc.tipo)} • {formatFileSize(doc.tamaño)}
                            </small>
                          </div>
                        </div>

                        {/* Acciones: Ver + Menú dropdown */}
                        <div className="d-flex gap-1 align-items-center">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleVer(doc)}
                            title="Ver documento"
                          >
                            <i className="bi bi-eye"></i>
                          </button>

                          {/* Menú de 3 puntos */}
                          <div className="dropdown">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              style={{ padding: '4px 8px' }}
                            >
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">
                              <li>
                                <button className="dropdown-item" onClick={() => handleCompartir(doc)}>
                                  <i className="bi bi-share me-2"></i>Compartir
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item" onClick={() => handleAbrirContacto(doc)}>
                                  <i className="bi bi-person me-2"></i>
                                  {doc.tieneContacto ? 'Editar contacto' : 'Agregar contacto'}
                                </button>
                              </li>
                              <li>
                                {(recordatoriosPorDoc[doc.id]?.length > 0) ? (
                                  <button className="dropdown-item" onClick={() => handleVerRecordatoriosDoc(doc)}>
                                    <i className="bi bi-bell-fill me-2"></i>
                                    Ver recordatorios ({recordatoriosPorDoc[doc.id].length})
                                  </button>
                                ) : (
                                  <button className="dropdown-item" onClick={() => handleAbrirRecordatorio(doc)}>
                                    <i className="bi bi-bell me-2"></i>
                                    {doc.tieneRecordatorio ? 'Editar recordatorio' : 'Agregar recordatorio'}
                                  </button>
                                )}
                              </li>
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <button className="dropdown-item text-danger" onClick={() => setDocumentoEliminar(doc)}>
                                  <i className="bi bi-trash me-2"></i>Eliminar
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Info de recordatorio (si existe) */}
                      {doc.tieneRecordatorio && doc.recordatorioVencimiento && (
                        <div
                          className="mt-2 d-flex align-items-center gap-2 px-2 py-1 rounded"
                          style={{ backgroundColor: '#fff3cd', fontSize: '0.85rem' }}
                        >
                          <i className="bi bi-bell-fill" style={{ color: '#856404' }}></i>
                          <span style={{ color: '#856404' }}>
                            <strong>Vence:</strong>{' '}
                            {new Date(doc.recordatorioVencimiento._seconds * 1000).toLocaleDateString('es-MX', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                            {doc.recordatorioDiasAnticipacion && (
                              <small className="ms-1">
                                (aviso {doc.recordatorioDiasAnticipacion} días antes)
                              </small>
                            )}
                          </span>
                        </div>
                      )}

                      {/* Badge de recordatorios de objeto vinculados */}
                      {recordatoriosPorDoc[doc.id]?.length > 0 && (
                        <div
                          className="mt-2 d-flex align-items-center gap-2 px-2 py-1 rounded"
                          style={{
                            backgroundColor: '#ecfccb',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onClick={() => handleVerRecordatoriosDoc(doc)}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#d9f99d'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ecfccb'; }}
                        >
                          <i className="bi bi-bell-fill" style={{ color: '#4d7c0f' }}></i>
                          <span style={{ color: '#4d7c0f' }}>
                            <strong>{recordatoriosPorDoc[doc.id].length}</strong>{' '}
                            recordatorio{recordatoriosPorDoc[doc.id].length !== 1 ? 's' : ''}
                          </span>
                          <i className="bi bi-chevron-right ms-auto" style={{ color: '#4d7c0f', fontSize: '0.75rem' }}></i>
                        </div>
                      )}

                      {/* Info de contacto (si existe) */}
                      {doc.tieneContacto && doc.contactoNombre && (
                        <div
                          className="mt-2 d-flex align-items-center gap-2 px-2 py-1 rounded"
                          style={{ backgroundColor: '#d1ecf1', fontSize: '0.85rem' }}
                        >
                          <i className="bi bi-person-fill" style={{ color: '#0c5460' }}></i>
                          <span style={{ color: '#0c5460' }}>
                            <strong>{doc.contactoNombre}</strong>
                            {doc.contactoRol && <span> - {doc.contactoRol}</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleClose}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Compartir */}
      {documentoCompartir && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-fullscreen-md-down">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">
                  <i className="bi bi-share me-2"></i>
                  Compartir: {documentoCompartir.nombre}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setDocumentoCompartir(null);
                    setLinksCompartir(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {loadingLinks ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm"></div>
                  </div>
                ) : linksCompartir ? (
                  <>
                    {/* Link de consulta */}
                    <div className="mb-3">
                      <label className="form-label small fw-bold">
                        <i className="bi bi-eye me-1"></i>
                        Solo consulta (no puede descargar)
                      </label>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          value={linksCompartir.consulta}
                          readOnly
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleCopiarLink(linksCompartir.consulta, 'consulta')}
                          title="Copiar"
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleVerQR(linksCompartir.consulta, 'Solo consulta')}
                          title="Ver QR"
                        >
                          <i className="bi bi-qr-code"></i>
                        </button>
                        <button
                          className="btn btn-outline-warning"
                          onClick={() => handleRegenerarToken('consulta')}
                          title="Regenerar link"
                        >
                          <i className="bi bi-arrow-clockwise"></i>
                        </button>
                      </div>
                    </div>

                    {/* Link de descarga */}
                    <div className="mb-3">
                      <label className="form-label small fw-bold">
                        <i className="bi bi-download me-1"></i>
                        Consulta + Descarga
                      </label>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          value={linksCompartir.descarga}
                          readOnly
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleCopiarLink(linksCompartir.descarga, 'descarga')}
                          title="Copiar"
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleVerQR(linksCompartir.descarga, 'Consulta + Descarga')}
                          title="Ver QR"
                        >
                          <i className="bi bi-qr-code"></i>
                        </button>
                        <button
                          className="btn btn-outline-warning"
                          onClick={() => handleRegenerarToken('descarga')}
                          title="Regenerar link"
                        >
                          <i className="bi bi-arrow-clockwise"></i>
                        </button>
                      </div>
                    </div>

                    <div className="alert alert-info small py-2">
                      <i className="bi bi-info-circle me-1"></i>
                      Regenerar un link invalida el anterior.
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar */}
      {documentoEliminar && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h6 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Eliminar documento
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setDocumentoEliminar(null);
                    setConfirmacionBorrar('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p className="small">
                  ¿Eliminar <strong>{documentoEliminar.nombre}</strong>?
                </p>
                <p className="small text-muted">
                  Esta acción no se puede deshacer. Los links de compartir dejarán de funcionar.
                </p>
                <div className="mb-2">
                  <label className="form-label small">
                    Escribe <strong>borrar</strong> para confirmar:
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={confirmacionBorrar}
                    onChange={(e) => setConfirmacionBorrar(e.target.value)}
                    placeholder="borrar"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setDocumentoEliminar(null);
                    setConfirmacionBorrar('');
                  }}
                  disabled={eliminando}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleEliminar}
                  disabled={eliminando || confirmacionBorrar !== 'borrar'}
                >
                  {eliminando ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal QR */}
      {qrModal.show && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100001, overflowY: 'auto' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">
                  <i className="bi bi-qr-code me-2"></i>
                  {qrModal.title}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setQrModal({ show: false, url: '', title: '' })}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img src={qrModal.url} alt="QR Code" className="img-fluid mb-2" />
                <p className="small text-muted text-break">{qrModal.link}</p>
              </div>
              <div className="modal-footer">
                <a
                  href={qrModal.url}
                  download={`qr-documento.png`}
                  className="btn btn-primary btn-sm"
                >
                  <i className="bi bi-download me-1"></i>
                  Descargar QR
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Contacto */}
      {documentoContacto && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-fullscreen-md-down">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h6 className="modal-title">
                  <i className="bi bi-person me-2"></i>
                  Contacto: {documentoContacto.nombre}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setDocumentoContacto(null);
                    setContactoForm({ nombre: '', rol: '', telefono: '', email: '', notas: '' });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label small">Nombre</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nombre del contacto"
                    value={contactoForm.nombre}
                    onChange={(e) => setContactoForm({ ...contactoForm, nombre: e.target.value })}
                    maxLength={100}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">Rol / Cargo</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ej: Agente, Asesor, Ejecutivo"
                    value={contactoForm.rol}
                    onChange={(e) => setContactoForm({ ...contactoForm, rol: e.target.value })}
                    maxLength={100}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">Teléfono</label>
                  <input
                    type="tel"
                    className="form-control form-control-sm"
                    placeholder="+52 555 123 4567"
                    value={contactoForm.telefono}
                    onChange={(e) => setContactoForm({ ...contactoForm, telefono: e.target.value })}
                    maxLength={20}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">Email</label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    placeholder="contacto@ejemplo.com"
                    value={contactoForm.email}
                    onChange={(e) => setContactoForm({ ...contactoForm, email: e.target.value })}
                    maxLength={100}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small">Notas</label>
                  <textarea
                    className="form-control form-control-sm"
                    placeholder="Notas adicionales..."
                    value={contactoForm.notas}
                    onChange={(e) => setContactoForm({ ...contactoForm, notas: e.target.value })}
                    rows={2}
                    maxLength={500}
                  />
                </div>
              </div>
              <div className="modal-footer">
                {documentoContacto.tieneContacto && (
                  <button
                    className="btn btn-outline-danger btn-sm me-auto"
                    onClick={handleEliminarContacto}
                    disabled={guardandoContacto}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Eliminar
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setDocumentoContacto(null);
                    setContactoForm({ nombre: '', rol: '', telefono: '', email: '', notas: '' });
                  }}
                  disabled={guardandoContacto}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-info btn-sm"
                  onClick={handleGuardarContacto}
                  disabled={guardandoContacto}
                >
                  {guardandoContacto ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Recordatorios de Objeto (lista) */}
      {verRecordatoriosDoc && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-fullscreen-md-down">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: 'var(--feature-primary, #84cc16)', color: 'white' }}>
                <h6 className="modal-title">
                  <i className="bi bi-bell-fill me-2"></i>
                  Recordatorios: {verRecordatoriosDoc.nombre}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => { setVerRecordatoriosDoc(null); setEditandoRecObj(null); setSelectedRecIds(new Set()); }}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {editandoRecObj ? (
                  /* === Formulario de edición === */
                  <>
                    <div className="mb-2">
                      <label className="form-label small">Descripción</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editRecObjForm.nombrePersonalizado}
                        onChange={(e) => setEditRecObjForm({ ...editRecObjForm, nombrePersonalizado: e.target.value })}
                        maxLength={200}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Fecha</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={editRecObjForm.fechaUnica}
                        onChange={(e) => setEditRecObjForm({ ...editRecObjForm, fechaUnica: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Monto (opcional)</label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          value={editRecObjForm.monto}
                          onChange={(e) => setEditRecObjForm({ ...editRecObjForm, monto: e.target.value })}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Notas</label>
                      <textarea
                        className="form-control form-control-sm"
                        value={editRecObjForm.notas}
                        onChange={(e) => setEditRecObjForm({ ...editRecObjForm, notas: e.target.value })}
                        rows={2}
                        maxLength={500}
                      />
                    </div>
                  </>
                ) : (
                  /* === Lista de recordatorios === */
                  <>
                    {(recordatoriosPorDoc[verRecordatoriosDoc.id] || []).length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-bell fs-1"></i>
                        <p className="mt-2 mb-0">No hay recordatorios para este documento</p>
                      </div>
                    ) : (
                      <>
                        {/* Seleccionar/Deseleccionar todos */}
                        <div className="mb-2 text-end">
                          <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ fontSize: '0.8rem' }}
                            onClick={() => handleSelectAllRecs(recordatoriosPorDoc[verRecordatoriosDoc.id] || [])}
                          >
                            {(recordatoriosPorDoc[verRecordatoriosDoc.id] || []).every(r => selectedRecIds.has(r.recordatorioId))
                              ? 'Deseleccionar todos'
                              : 'Seleccionar todos'}
                          </button>
                        </div>
                        <div className="list-group">
                          {(recordatoriosPorDoc[verRecordatoriosDoc.id] || []).map((rec) => (
                            <div key={rec.recordatorioId} className="list-group-item py-2">
                              <div className="d-flex align-items-start">
                                {/* Checkbox */}
                                <div className="me-2" style={{ paddingTop: '2px' }}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedRecIds.has(rec.recordatorioId)}
                                    onChange={() => handleToggleRecSelect(rec.recordatorioId)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </div>
                                <div className="flex-grow-1 me-2">
                                  <div className="fw-medium" style={{ fontSize: '0.9rem' }}>
                                    {rec.nombrePersonalizado || 'Recordatorio'}
                                  </div>
                                  <small className="text-muted">
                                    {rec.fechaUnica && new Date(rec.fechaUnica + 'T12:00:00').toLocaleDateString('es-MX', {
                                      day: 'numeric', month: 'long', year: 'numeric'
                                    })}
                                    {rec.monto != null && (
                                      <span className="ms-2">
                                        ${parseFloat(rec.monto).toLocaleString('es-MX')} {rec.moneda || 'MXN'}
                                      </span>
                                    )}
                                  </small>
                                  {rec.notas && (
                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>{rec.notas}</div>
                                  )}
                                </div>
                                <div className="d-flex gap-1">
                                  <button
                                      className="btn btn-sm btn-outline-dark"
                                      onClick={() => handleDescargarWalletPassDirecto(verRecordatoriosDoc.id, rec.recordatorioId)}
                                      disabled={descargandoWalletRecId === rec.recordatorioId}
                                      title="Descargar pase de Wallet"
                                      style={{ padding: '2px 6px' }}
                                    >
                                      {descargandoWalletRecId === rec.recordatorioId ? (
                                        <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
                                      ) : (
                                        <i className="bi bi-wallet2"></i>
                                      )}
                                    </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleEditarRecObj(rec)}
                                    title="Editar"
                                    style={{ padding: '2px 6px' }}
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleEliminarRecObj(rec)}
                                    disabled={eliminandoRecObj === rec.recordatorioId}
                                    title="Eliminar"
                                    style={{ padding: '2px 6px' }}
                                  >
                                    {eliminandoRecObj === rec.recordatorioId ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : (
                                      <i className="bi bi-trash"></i>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                {editandoRecObj ? (
                  <>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditandoRecObj(null)}
                      disabled={guardandoRecObj}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn btn-sm text-white"
                      style={{ backgroundColor: 'var(--feature-primary, #84cc16)' }}
                      onClick={handleGuardarRecObj}
                      disabled={guardandoRecObj || !editRecObjForm.nombrePersonalizado.trim()}
                    >
                      {guardandoRecObj ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Botón eliminar todos del grupo */}
                    {(() => {
                      const recs = recordatoriosPorDoc[verRecordatoriosDoc.id] || [];
                      const grupoId = recs[0]?.grupoRecordatorio;
                      if (grupoId && recs.length > 1) {
                        return (
                          <button
                            className="btn btn-outline-danger btn-sm me-auto"
                            onClick={() => handleEliminarGrupoRec(grupoId)}
                            disabled={guardandoRecObj}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Eliminar todos ({recs.length})
                          </button>
                        );
                      }
                      return null;
                    })()}
                    {/* Botón compartir seleccionados */}
                    {selectedRecIds.size > 0 && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleCompartirRecordatorios}
                        disabled={loadingCompartir}
                      >
                        {loadingCompartir ? (
                          <span className="spinner-border spinner-border-sm me-1"></span>
                        ) : (
                          <i className="bi bi-share me-1"></i>
                        )}
                        Compartir ({selectedRecIds.size})
                      </button>
                    )}
                    <button
                      className="btn btn-sm text-white"
                      style={{ backgroundColor: 'var(--feature-primary, #84cc16)' }}
                      onClick={() => {
                        setVerRecordatoriosDoc(null);
                        setSelectedRecIds(new Set());
                        handleAbrirRecordatorio(verRecordatoriosDoc);
                      }}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      Agregar más
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setVerRecordatoriosDoc(null); setEditandoRecObj(null); setSelectedRecIds(new Set()); }}
                    >
                      Cerrar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Compartir Recordatorios */}
      {compartirModal && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100001, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-fullscreen-md-down">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: 'var(--feature-primary, #84cc16)', color: 'white' }}>
                <h6 className="modal-title">
                  <i className="bi bi-share me-2"></i>
                  Compartir recordatorios
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setCompartirModal(null)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <p className="text-muted small mb-3">
                  Se compartirán <strong>{compartirModal.count}</strong> recordatorio{compartirModal.count !== 1 ? 's' : ''} de <strong>{objetoNombre}</strong>
                </p>

                {/* Link copiable */}
                <div className="input-group input-group-sm mb-3">
                  <input
                    type="text"
                    className="form-control"
                    value={compartirModal.shareUrl}
                    readOnly
                    style={{ fontSize: '0.8rem' }}
                  />
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleCopiarLinkCompartir}
                    title="Copiar link"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>

                {/* QR Code */}
                {compartirModal.qrDataUrl && (
                  <div className="mb-3">
                    <img
                      src={compartirModal.qrDataUrl}
                      alt="QR Code"
                      style={{ width: '200px', height: '200px', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}
                    />
                  </div>
                )}

                <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Quien abra este link podrá descargar los recordatorios como archivo de calendario (.ics)
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-danger btn-sm me-auto"
                  onClick={handleRevocarLink}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Revocar link
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCompartirModal(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Recordatorio - Multi-step */}
      {documentoRecordatorio && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-fullscreen-md-down">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: 'var(--feature-primary, #84cc16)', color: 'white' }}>
                <h6 className="modal-title">
                  <i className="bi bi-bell me-2"></i>
                  Recordatorio: {documentoRecordatorio.nombre}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setDocumentoRecordatorio(null);
                    setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
                    setRecordatorioMetodo(null);
                    setAnalisisError(null);
                    setAnalisisResultado(null);
                    setRecurrenciaForm('unico');
                  }}
                ></button>
              </div>
              <div className="modal-body">

                {/* === PASO 1: Selector de método === */}
                {recordatorioMetodo === 'selector' && (
                  <>
                    <p className="text-muted small mb-3">
                      ¿Cómo quieres crear el recordatorio?
                    </p>

                    {/* Opción: Desde el documento */}
                    <div
                      className="card mb-2"
                      style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                      onClick={handleAnalizarDocumento}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div className="card-body py-3">
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>
                            <i className="bi bi-file-earmark-text"></i>
                          </span>
                          <div>
                            <div className="fw-semibold">Desde el documento</div>
                            <small className="text-muted">
                              Analizamos el documento para encontrar fechas de vencimiento
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Opción: Manual */}
                    <div
                      className="card"
                      style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                      onClick={handleRecordatorioManual}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div className="card-body py-3">
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>
                            <i className="bi bi-pencil"></i>
                          </span>
                          <div>
                            <div className="fw-semibold">Manual</div>
                            <small className="text-muted">
                              Ingresa la descripción y fecha tú mismo
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* === PASO 2a: Analizando (loading) === */}
                {recordatorioMetodo === 'auto' && analizandoDoc && (
                  <div className="text-center py-4">
                    <div className="spinner-border mb-3" style={{ color: 'var(--feature-primary, #84cc16)' }} role="status">
                      <span className="visually-hidden">Analizando...</span>
                    </div>
                    <p className="text-muted mb-0">Analizando documento...</p>
                    <small className="text-muted">Esto puede tomar unos segundos</small>
                  </div>
                )}

                {/* === PASO 2b: Error en análisis === */}
                {recordatorioMetodo === 'auto' && !analizandoDoc && analisisError && (
                  <div className="text-center py-3">
                    <div className="alert alert-warning small py-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {analisisError}
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleVolverSelector}
                      >
                        <i className="bi bi-arrow-left me-1"></i>
                        Volver
                      </button>
                      <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: 'var(--feature-primary, #84cc16)' }}
                        onClick={handleRecordatorioManual}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Crear manualmente
                      </button>
                    </div>
                  </div>
                )}

                {/* === PASO 3: Resultado del análisis === */}
                {recordatorioMetodo === 'auto' && !analizandoDoc && analisisResultado && (
                  <>
                    <div className="alert alert-success small py-2 mb-3">
                      <i className="bi bi-check-circle me-1"></i>
                      Detectamos información de vencimiento
                      {analisisResultado.tipoDocumento && (
                        <span className="ms-1">
                          — <strong>{analisisResultado.tipoDocumento}</strong>
                        </span>
                      )}
                    </div>

                    {analisisResultado.advertencia && (
                      <div className="alert alert-info small py-2 mb-3">
                        <i className="bi bi-info-circle me-1"></i>
                        {analisisResultado.advertencia}
                      </div>
                    )}

                    {/* === MULTI-PAYMENT VIEW === */}
                    {analisisResultado.pagosDetectados && analisisResultado.pagosDetectados.length > 0 ? (
                      <>
                        <label className="form-label small fw-semibold mb-2">Recordatorios a crear:</label>
                        <div className="list-group mb-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          {analisisResultado.pagosDetectados.map((pago, idx) => (
                            <label
                              key={idx}
                              className="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2"
                              style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={!!pagosSeleccionados[idx]}
                                onChange={() => setPagosSeleccionados(prev => ({ ...prev, [idx]: !prev[idx] }))}
                              />
                              <div className="flex-grow-1">
                                <div className="fw-medium">{pago.descripcion}</div>
                                <small className="text-muted">
                                  {new Date(pago.fecha + 'T12:00:00').toLocaleDateString('es-MX', {
                                    day: 'numeric', month: 'long', year: 'numeric'
                                  })}
                                </small>
                              </div>
                              {montoEditable && (
                                <span className="text-muted small">
                                  ${parseFloat(montoEditable).toLocaleString('es-MX')} {analisisResultado.moneda || 'MXN'}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>

                        <div className="mb-2">
                          <label className="form-label small">Monto por pago (opcional)</label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="0.00"
                              value={montoEditable}
                              onChange={(e) => setMontoEditable(e.target.value)}
                              min="0"
                              step="0.01"
                            />
                            <span className="input-group-text">{analisisResultado.moneda || 'MXN'}</span>
                          </div>
                        </div>

                        <div className="mb-2">
                          <label className="form-label small">Avisar con anticipación de</label>
                          <select
                            className="form-select form-select-sm"
                            value={recordatorioForm.diasAnticipacion}
                            onChange={(e) => setRecordatorioForm({ ...recordatorioForm, diasAnticipacion: e.target.value })}
                          >
                            <option value="7">7 días antes</option>
                            <option value="15">15 días antes</option>
                            <option value="30">30 días antes</option>
                          </select>
                        </div>

                        {analisisResultado.notas && (
                          <div className="alert alert-light small py-2 mb-0 border">
                            <i className="bi bi-chat-left-text me-1"></i>
                            {analisisResultado.notas}
                          </div>
                        )}
                      </>
                    ) : (
                      /* === SINGLE REMINDER FORM (no pagosDetectados) === */
                      <>
                        <div className="mb-2">
                          <label className="form-label small">Descripción *</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Ej: Renovación de póliza, Pago anual"
                            value={recordatorioForm.descripcion}
                            onChange={(e) => setRecordatorioForm({ ...recordatorioForm, descripcion: e.target.value })}
                            maxLength={200}
                          />
                        </div>
                        <div className="mb-2">
                          <label className="form-label small">Fecha de vencimiento *</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={recordatorioForm.fechaVencimiento}
                            onChange={(e) => setRecordatorioForm({ ...recordatorioForm, fechaVencimiento: e.target.value })}
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div className="mb-2">
                          <label className="form-label small">Se repite</label>
                          <select
                            className="form-select form-select-sm"
                            value={recurrenciaForm}
                            onChange={(e) => setRecurrenciaForm(e.target.value)}
                          >
                            <option value="unico">No se repite</option>
                            <option value="mensual">Mensual</option>
                            <option value="bimestral">Bimestral</option>
                            <option value="trimestral">Trimestral</option>
                            <option value="semestral">Semestral</option>
                            <option value="anual">Anual</option>
                          </select>
                        </div>
                        <div className="mb-2">
                          <label className="form-label small">Avisar con anticipación de</label>
                          <select
                            className="form-select form-select-sm"
                            value={recordatorioForm.diasAnticipacion}
                            onChange={(e) => setRecordatorioForm({ ...recordatorioForm, diasAnticipacion: e.target.value })}
                          >
                            <option value="7">7 días antes</option>
                            <option value="15">15 días antes</option>
                            <option value="30">30 días antes</option>
                          </select>
                        </div>

                        {analisisResultado.notas && (
                          <div className="alert alert-light small py-2 mb-0 border">
                            <i className="bi bi-chat-left-text me-1"></i>
                            {analisisResultado.notas}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* === Formulario manual (original) === */}
                {recordatorioMetodo === 'manual' && (
                  <>
                    <div className="mb-2">
                      <label className="form-label small">Descripción *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Ej: Renovación de póliza, Pago anual"
                        value={recordatorioForm.descripcion}
                        onChange={(e) => setRecordatorioForm({ ...recordatorioForm, descripcion: e.target.value })}
                        maxLength={200}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Fecha de vencimiento *</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={recordatorioForm.fechaVencimiento}
                        onChange={(e) => setRecordatorioForm({ ...recordatorioForm, fechaVencimiento: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Avisar con anticipación de</label>
                      <select
                        className="form-select form-select-sm"
                        value={recordatorioForm.diasAnticipacion}
                        onChange={(e) => setRecordatorioForm({ ...recordatorioForm, diasAnticipacion: e.target.value })}
                      >
                        <option value="7">7 días antes</option>
                        <option value="15">15 días antes</option>
                        <option value="30">30 días antes</option>
                      </select>
                    </div>
                    <div className="alert alert-info small py-2 mb-0">
                      <i className="bi bi-info-circle me-1"></i>
                      Recibirás un correo de recordatorio según la anticipación seleccionada.
                    </div>
                  </>
                )}

              </div>

              {/* Footer - only show when form is visible (manual or auto with result) */}
              {(recordatorioMetodo === 'manual' || (recordatorioMetodo === 'auto' && analisisResultado)) && (
              <div className="modal-footer">
                {documentoRecordatorio.tieneRecordatorio && recordatorioMetodo === 'manual' && (
                  <button
                    className="btn btn-outline-danger btn-sm me-auto"
                    onClick={handleEliminarRecordatorio}
                    disabled={guardandoRecordatorio}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Eliminar
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setDocumentoRecordatorio(null);
                    setRecordatorioForm({ descripcion: '', fechaVencimiento: '', diasAnticipacion: '15' });
                    setRecordatorioMetodo(null);
                    setAnalisisError(null);
                    setAnalisisResultado(null);
                    setRecurrenciaForm('unico');
                    setPagosSeleccionados({});
                    setMontoEditable('');
                  }}
                  disabled={guardandoRecordatorio}
                >
                  Cancelar
                </button>
                {/* Multi-payment button or single reminder button */}
                {analisisResultado?.pagosDetectados?.length > 0 ? (
                  <button
                    className="btn btn-sm text-white"
                    style={{ backgroundColor: 'var(--feature-primary, #84cc16)' }}
                    onClick={handleGuardarPagosLote}
                    disabled={guardandoRecordatorio || Object.values(pagosSeleccionados).filter(Boolean).length === 0}
                  >
                    {guardandoRecordatorio ? 'Guardando...' : `Crear ${Object.values(pagosSeleccionados).filter(Boolean).length} recordatorio${Object.values(pagosSeleccionados).filter(Boolean).length !== 1 ? 's' : ''}`}
                  </button>
                ) : (
                  <button
                    className="btn btn-sm text-white"
                    style={{ backgroundColor: 'var(--feature-primary, #84cc16)' }}
                    onClick={handleGuardarRecordatorio}
                    disabled={guardandoRecordatorio}
                  >
                    {guardandoRecordatorio ? 'Guardando...' : 'Crear recordatorio'}
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Apple Wallet Offer */}
      {walletOffer && (
        <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100001, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-body text-center py-4 px-3">
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                  <i className="bi bi-check-circle-fill" style={{ color: 'var(--feature-primary, #84cc16)' }}></i>
                </div>
                <h6 className="fw-bold mb-2">
                  {walletOffer.isUpdate ? 'Recordatorio actualizado' : 'Recordatorio creado'}
                </h6>
                <p className="text-muted small mb-3">
                  {walletOffer.isUpdate
                    ? 'Actualiza el pase de Wallet para reflejar los cambios.'
                    : 'Agregar a Apple Wallet para recibir una notificación cuando se acerque el vencimiento.'}
                </p>

                {walletOffer.recordatorioIds.length === 1 ? (
                  <button
                    className="btn d-flex align-items-center justify-content-center gap-2 mx-auto mb-2"
                    onClick={() => handleDescargarWalletPass(walletOffer.recordatorioIds[0])}
                    disabled={descargandoWallet}
                    style={{
                      backgroundColor: '#000',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 500,
                      opacity: descargandoWallet ? 0.7 : 1
                    }}
                  >
                    {descargandoWallet ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        Generando...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                        {walletOffer.isUpdate ? 'Actualizar pase' : 'Agregar a Wallet'}
                      </>
                    )}
                  </button>
                ) : (
                  <p className="text-muted small mb-2">
                    Se crearon {walletOffer.recordatorioIds.length} recordatorios.
                    Puedes agregarlos a Wallet desde la lista de recordatorios del documento.
                  </p>
                )}

                <button
                  className="btn btn-link text-muted small"
                  onClick={() => setWalletOffer(null)}
                  disabled={descargandoWallet}
                >
                  No, gracias
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
